import { ReactNode, createContext, useCallback, useMemo } from 'react';

import mixpanel, { Config } from 'mixpanel-browser';
import { useMount } from 'react-use';

import { FullStory } from '@fullstory/browser';

import { getEnv } from '@/components/shared/PrimaryNavDrawer/utils';
import { useSession } from '@/providers/SessionProvider/SessionProvider';

type MixpanelContextType = {
  track: (eventName: string, data?: Record<string, unknown>) => void;
};

type MixpanelConfig = Partial<Config> & {
  autocapture?: boolean;
};

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

export const MixpanelContext = createContext<MixpanelContextType | undefined>(
  undefined,
);

export const MixpanelProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser, currentClient } = useSession();
  const { disableMixpanelSessionRecord } = currentClient ?? {};
  const env = getEnv();
  if (!MIXPANEL_TOKEN) {
    console.error('Missing Mixpanel Token');
  }

  const shouldNotTrack = !MIXPANEL_TOKEN || !currentUser || env !== 'prod';

  useMount(() => {
    if (shouldNotTrack) return;

    let mixpanelConfig: MixpanelConfig = {
      autotrack: true,
      track_pageview: true,
      debug: !!window.Cypress,
      autocapture: true,
    };

    if (!disableMixpanelSessionRecord) {
      mixpanelConfig = {
        ...mixpanelConfig,
        record_sessions_percent: 5,
        record_mask_text_selector: "input[type='password'], .sensitive-data",
      };
    }

    mixpanel.init(MIXPANEL_TOKEN, mixpanelConfig);
    window.mixpanel = mixpanel;

    if (currentUser) {
      const { id, email, fullName, isStaff } = currentUser;

      mixpanel.identify(id.toString());
      mixpanel.people.set({ $name: fullName, $email: email, isStaff });
      mixpanel.register({
        user_id: id,
        client_name: currentClient?.name,
        client_display_name: currentClient?.displayName,
        // Lets any Mixpanel event be traced back to its session replay.
        fullstory_url: FullStory('getSession', { format: 'url' }),
      });
    }

    if (window.Appcues) {
      window.Appcues.on('flow_started', (flow) => {
        mixpanel.track('Appcues Flow Started', {
          flowId: flow.id,
          flowName: flow.name,
        });
      });
      window.Appcues.on('flow_completed', (flow) => {
        mixpanel.track('Appcues Flow Completed', {
          flowId: flow.id,
          flowName: flow.name,
        });
      });
    }
  });

  const track = useCallback(
    (eventName: string, data?: Record<string, unknown>) => {
      // Mixpanel is only initialised in prod, so everywhere else the console
      // is the only way to see an event. Gated on env rather than
      // `shouldNotTrack`, which is briefly true in prod before the session
      // resolves — production should never log.
      if (env !== 'prod') {
        console.debug('[mixpanel]', eventName, data);
        return;
      }

      if (shouldNotTrack) return;

      mixpanel.track(eventName, data);
    },
    [env, shouldNotTrack],
  );

  // Stable identity: consumers use `track` in effect and callback deps.
  const value = useMemo(() => ({ track }), [track]);

  return (
    <MixpanelContext.Provider value={value}>
      {children}
    </MixpanelContext.Provider>
  );
};

export default MixpanelProvider;
